import { useQuery } from "@tanstack/react-query";
import { Trash2 } from "lucide-react";
import { type ChangeEvent, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { MultiSelect } from "@/components/ui/multi-select";
import { TextInput } from "@/components/ui/textInput";
import { additionalNotesQueryOptions } from "@/hooks/useAdditionalNotes";
import useCUCompositeJobStore from "@/store/useCUCompositeJobStore";

import ExpandableSection from "../../../../components/job/ExpandableSection";

const cardName = "notes";

type NoteSchema = { id?: string; custom_name_of_note?: string; custom_note?: string };

type NoteComponentProps = {
  index: number;
  data: NoteSchema;
  onChange: (index: number, data: NoteSchema) => void;
  onDelete: (index: number) => void;
};

function NoteComponent({ index, data, onChange, onDelete }: NoteComponentProps) {
  const handleChange = (key: keyof NoteSchema, value: string) => {
    onChange(index, { ...data, [key]: value });
  };

  const handleDelete = () => {
    onDelete(index);
  };

  return (
    <div className="my-4 rounded-lg border border-gray-200 bg-white p-4">
      <div className="flex items-center justify-between gap-3">
        <span className="text-sm font-semibold text-gray-700">Note {index + 1}</span>
        <Button
          variant="outline"
          size="icon"
          type="button"
          onClick={handleDelete}
          aria-label="Delete note"
          title="Delete note"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
      <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2 md:items-end">
        <TextInput
          label="Note"
          value={data.custom_note ?? ""}
          onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange("custom_note", e.target.value)}
        />
        <TextInput
          label="Name of note"
          value={data.custom_name_of_note ?? ""}
          onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange("custom_name_of_note", e.target.value)}
        />
      </div>
    </div>
  );
}

export function Notes() {
  const { sections, toggleExpandableSection, setNotes } = useCUCompositeJobStore();
  const savedNotes = sections.notes;

  const { data: { mappedNotesOptions = [] } = {} } = useQuery({
    ...additionalNotesQueryOptions("composite"),
    select: (data) => ({
      mappedNotesOptions: data?.additional_notes?.map((note) => ({ label: note.name, value: String(note.id) })) ?? [],
    }),
  });

  const [additional_notes, setAdditional_notes] = useState<Array<string>>(
    savedNotes.data.additional_notes?.map(String) ?? []
  );
  const [noteComponents, setNoteComponents] = useState<NoteSchema[]>(
    savedNotes.data.new_notes?.length
      ? savedNotes.data.new_notes.map((note) => ({ ...note, id: note.id ?? crypto.randomUUID() }))
      : [{ id: crypto.randomUUID() }]
  );

  useEffect(() => {
    // If editing a draft, load saved selections
    if (savedNotes.isDataFilled) {
      setAdditional_notes(savedNotes.data.additional_notes?.map(String) ?? []);
      setNoteComponents(
        savedNotes.data.new_notes?.length
          ? savedNotes.data.new_notes.map((note) => ({ ...note, id: note.id ?? crypto.randomUUID() }))
          : [{ id: crypto.randomUUID() }]
      );
    }
  }, [savedNotes.data.additional_notes, savedNotes.data.new_notes, savedNotes.isDataFilled]);

  function handleAddNote() {
    setNoteComponents([...noteComponents, { id: crypto.randomUUID(), custom_name_of_note: "", custom_note: "" }]);
  }

  function handleNoteChange(index: number, data: NoteSchema) {
    const updatedComponents = [...noteComponents];
    updatedComponents[index] = data;
    setNoteComponents(updatedComponents);
  }

  function handleNoteDelete(index: number) {
    const updatedComponents = [...noteComponents];
    updatedComponents.splice(index, 1);
    setNoteComponents(updatedComponents);
  }

  function handleSaveNotes() {
    const customNotes = noteComponents.filter((n) => n.custom_name_of_note || n.custom_note);

    setNotes({
      data: {
        additional_notes: additional_notes.map((note) => Number(note)).filter((n) => !Number.isNaN(n)),
        new_notes: customNotes,
      },
      status: "done",
    });
  }

  return (
    <ExpandableSection
      setIsExpanded={() => toggleExpandableSection(cardName)}
      status={savedNotes?.status ?? "blocked"}
      title="Notes"
    >
      <Label>Additional notes</Label>
      <MultiSelect
        value={additional_notes}
        defaultValue={additional_notes}
        options={mappedNotesOptions}
        onValueChange={(selected) => setAdditional_notes(selected)}
      />

      {noteComponents.map((note, index) => (
        <NoteComponent
          key={note.id}
          index={index}
          data={note}
          onChange={handleNoteChange}
          onDelete={handleNoteDelete}
        />
      ))}

      <div className="flex justify-end gap-2">
        <Button variant="outline" className="mt-6" onClick={handleAddNote}>
          Add Note
        </Button>
        <Button className="mt-6" onClick={handleSaveNotes}>
          Save
        </Button>
      </div>
    </ExpandableSection>
  );
}
